import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Tenant {
  id: string;
  userId: string;
  companyName: string;
  phoneNumber: string;
  createdAt: string;
  updatedAt: string;
}

interface User {
  id: string;
  email: string;
  isAdmin: boolean;
  approved: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TenantSettingsProps {
  userId?: string;
}

const TenantSettings: React.FC<TenantSettingsProps> = ({ userId }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneUpdateLoading, setPhoneUpdateLoading] = useState(false);
  const [passwordUpdateLoading, setPasswordUpdateLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneSuccess, setPhoneSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/user/profile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to fetch profile');
      }

      setUser(data.data.user);
      setTenant(data.data.tenant);
      if (data.data.tenant) {
        setPhoneNumber(data.data.tenant.phoneNumber || '');
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneNumberSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPhoneError('');
    setPhoneSuccess('');

    if (!phoneNumber || phoneNumber.trim() === '') {
      setPhoneError('Phone number is required');
      return;
    }

    setPhoneUpdateLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to update phone number');
      }

      setPhoneSuccess('Phone number updated successfully!');
      await fetchProfile(); // Refresh profile
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Failed to update phone number');
    } finally {
      setPhoneUpdateLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setPasswordUpdateLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      // First verify current password by attempting to sign in
      const verifyResponse = await fetch(`${API_URL}/user/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user?.email,
          password: currentPassword,
        }),
      });

      const verifyData = await verifyResponse.json();

      if (!verifyResponse.ok || !verifyData.success) {
        throw new Error('Current password is incorrect');
      }

      // Update password
      const updateResponse = await fetch(`${API_URL}/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          password: newPassword,
        }),
      });

      const updateData = await updateResponse.json();

      if (!updateResponse.ok || !updateData.success) {
        throw new Error(updateData.error?.message || 'Failed to update password');
      }

      setPasswordSuccess('Password updated successfully!');

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setPasswordUpdateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-3xl mx-auto p-8">
        <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-8 shadow-xl backdrop-blur-sm">
          <div className="text-gray-400 text-center">Loading...</div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="w-full max-w-3xl mx-auto p-8">
        <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-8 shadow-xl backdrop-blur-sm">
          <div className="p-4 bg-red-500/10 backdrop-blur-sm rounded-xl text-red-200 text-sm shadow-lg border-l-4 border-red-500">
            {loadError}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-3xl mx-auto p-8">
      <div className="mb-4">
        <Link
          to="/dashboard"
          className="py-2 text-purple-600 hover:text-purple-700 font-medium rounded transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Dashboard
        </Link>
      </div>
      <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-8 shadow-xl backdrop-blur-sm">
        <h2 className="text-3xl font-bold text-white mb-8 tracking-tight">
          Settings
        </h2>

        <div className="space-y-6">
          {/* Company Name - Read Only */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">Company Information</h3>
            <div className="space-y-4">
              <div className="flex flex-col gap-2">
                <label htmlFor="companyName" className="text-sm font-medium text-gray-300">
                  Company Name
                </label>
                <input
                  type="text"
                  id="companyName"
                  value={tenant?.companyName || ''}
                  disabled
                  className="px-4 py-3 bg-main-light/30 backdrop-blur-sm rounded-xl text-gray-400 cursor-not-allowed shadow-md"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-300">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="px-4 py-3 bg-main-light/30 backdrop-blur-sm rounded-xl text-gray-400 cursor-not-allowed shadow-md"
                />
              </div>
            </div>
          </div>

          {/* Phone Number - Editable */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">Update Phone Number</h3>
            <form onSubmit={handlePhoneNumberSubmit} className="space-y-5">
              {phoneError && (
                <div className="p-4 bg-red-500/10 backdrop-blur-sm rounded-xl text-red-200 text-sm shadow-lg border-l-4 border-red-500">
                  {phoneError}
                </div>
              )}
              {phoneSuccess && (
                <div className="p-4 bg-green-500/10 backdrop-blur-sm rounded-xl text-green-200 text-sm shadow-lg border-l-4 border-green-500">
                  {phoneSuccess}
                </div>
              )}

              <div className="flex flex-col gap-2">
                <label htmlFor="phoneNumber" className="text-sm font-medium text-gray-300">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                  disabled={phoneUpdateLoading}
                  placeholder="Enter your phone number"
                  className="px-4 py-3 bg-main-light/50 backdrop-blur-sm rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-light/70 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all duration-200"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed"
                disabled={phoneUpdateLoading}
              >
                {phoneUpdateLoading ? 'Updating...' : 'Update Phone Number'}
              </button>
            </form>
          </div>

          {/* Password - Editable */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">Change Password</h3>
            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              {passwordError && (
                <div className="p-4 bg-red-500/10 backdrop-blur-sm rounded-xl text-red-200 text-sm shadow-lg border-l-4 border-red-500">
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="p-4 bg-green-500/10 backdrop-blur-sm rounded-xl text-green-200 text-sm shadow-lg border-l-4 border-green-500">
                  {passwordSuccess}
                </div>
              )}

              <div className="flex flex-col gap-2">
                <label htmlFor="currentPassword" className="text-sm font-medium text-gray-300">
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  disabled={passwordUpdateLoading}
                  placeholder="Enter current password"
                  className="px-4 py-3 bg-main-light/50 backdrop-blur-sm rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-light/70 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all duration-200"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="newPassword" className="text-sm font-medium text-gray-300">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={passwordUpdateLoading}
                  placeholder="Enter new password (min 6 characters)"
                  minLength={6}
                  className="px-4 py-3 bg-main-light/50 backdrop-blur-sm rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-light/70 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all duration-200"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-300">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={passwordUpdateLoading}
                  placeholder="Confirm new password"
                  minLength={6}
                  className="px-4 py-3 bg-main-light/50 backdrop-blur-sm rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-light/70 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all duration-200"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed"
                disabled={passwordUpdateLoading}
              >
                {passwordUpdateLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantSettings;
